// Checkout TRANSPARENTE (in-app): nunca redireciona para a página hospedada do
// Stripe. Renderiza o <PaymentElement/> (cartão + Pix + boleto, conforme a conta
// Stripe habilita) dentro de um diálogo. Em "pagamento" confirma um PaymentIntent
// (carrinho ou fatura avulsa); em "setup" salva um cartão via SetupIntent.
import { useEffect, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Appearance, StripeElementsOptions } from '@stripe/stripe-js';
import { Loader2, Lock, CreditCard, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { stripePromiseFor } from '@/lib/stripe';
import { useI18n } from '@/lib/i18n';

const stripePromise = stripePromiseFor();

// Aparência do PaymentElement alinhada à marca bZapper (âmbar/ink). Bordas NÍTIDAS nos campos
// (o tema 'flat' deixava os inputs sem borda → baixo contraste no fundo branco) + label escura,
// placeholder legível e aba selecionada em âmbar.
const appearance: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#e0780c',
    colorText: '#17140d',
    colorTextSecondary: '#5c5546',
    colorTextPlaceholder: '#9b927f',
    colorBackground: '#ffffff',
    colorDanger: '#c2410c',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSizeBase: '15px',
    borderRadius: '12px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      border: '1.5px solid #cbc0a6',
      boxShadow: 'none',
      color: '#17140d',
      padding: '11px 12px',
    },
    '.Input:focus': {
      border: '1.5px solid #e0780c',
      boxShadow: '0 0 0 3px rgba(224,120,12,0.18)',
    },
    '.Input::placeholder': { color: '#a89e89' },
    '.Input--invalid': { border: '1.5px solid #c2410c' },
    '.Label': {
      color: '#3f3a30',
      fontWeight: '600',
      fontSize: '13px',
    },
    '.Tab': {
      border: '1.5px solid #e3dac6',
      boxShadow: 'none',
      color: '#5c5546',
    },
    '.Tab:hover': { border: '1.5px solid #cbc0a6', color: '#17140d' },
    '.Tab--selected': {
      border: '1.5px solid #e0780c',
      backgroundColor: 'rgba(224,120,12,0.07)',
      color: '#17140d',
    },
    '.TabLabel': { fontWeight: '600' },
    '.Block': { border: '1.5px solid #e3dac6', boxShadow: 'none' },
  },
};

/** Cartão salvo do tenant (subset do PaymentMethod; evita ciclo de import). */
export interface SavedCard {
  stripe_payment_method_id: string;
  brand: string;
  last4: string;
  is_default?: boolean;
  status?: 'ok' | 'problem';
}

export interface CheckoutTarget {
  clientSecret: string;
  /** 'payment' confirma um PaymentIntent; 'setup' salva um cartão (SetupIntent). */
  mode: 'payment' | 'setup';
  /** Para onde o Stripe volta após métodos com redirect (Pix/boleto). */
  returnUrl: string;
  /** Mostrar o toggle "salvar cartão para cobrança automática" (só no modo payment). */
  showSaveToggle?: boolean;
  /** Estado inicial do toggle (refletindo o save_card já enviado ao backend). */
  saveCardDefault?: boolean;
  /** Título e descrição do diálogo. */
  title?: string;
  description?: string;
  /** Texto do botão de confirmar (ex.: "Pagar R$ 49,00"). */
  confirmLabel?: string;
}

export function CheckoutDialog({
  target,
  savedCards = [],
  onClose,
  onSuccess,
  onSaveCardChange,
}: {
  target: CheckoutTarget | null;
  /** Cartões já salvos — oferecidos como opção no modo pagamento. */
  savedCards?: SavedCard[];
  onClose: () => void;
  /** Disparado quando o pagamento/setup é confirmado SEM redirect (cartão). */
  onSuccess?: () => void;
  /** Avisa o pai quando o usuário alterna "salvar cartão" (para reenviar o checkout). */
  onSaveCardChange?: (save: boolean) => void;
}) {
  const { t } = useI18n();
  const options: StripeElementsOptions | null = useMemo(
    () => (target ? { clientSecret: target.clientSecret, appearance } : null),
    [target],
  );

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="size-4 text-primary" />
            {target?.title ?? t('billing.checkoutTitle')}
          </DialogTitle>
          <DialogDescription>
            {target?.description ?? t('billing.checkoutDesc')}
          </DialogDescription>
        </DialogHeader>
        {target && options ? (
          // key força remount limpo do Element quando o client_secret muda (novo carrinho/fatura).
          <Elements key={target.clientSecret} stripe={stripePromise} options={options}>
            <CheckoutForm
              target={target}
              savedCards={savedCards}
              onSuccess={onSuccess}
              onSaveCardChange={onSaveCardChange}
            />
          </Elements>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function CheckoutForm({
  target,
  savedCards,
  onSuccess,
  onSaveCardChange,
}: {
  target: CheckoutTarget;
  savedCards: SavedCard[];
  onSuccess?: () => void;
  onSaveCardChange?: (save: boolean) => void;
}) {
  const { t } = useI18n();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [saveCard, setSaveCard] = useState(!!target.saveCardDefault);

  // Cartões salvos só valem no modo PAGAMENTO (o setup é para adicionar cartão novo).
  const cards = useMemo(() => (target.mode === 'payment' ? savedCards : []), [target.mode, savedCards]);
  // Seleção: por padrão o PRINCIPAL (ou o 1º cartão); 'new' = cartão novo / outro método.
  const defaultPm = useMemo(() => {
    if (!cards.length) return 'new';
    return (cards.find((c) => c.is_default) ?? cards[0]).stripe_payment_method_id;
  }, [cards]);
  const [selectedPm, setSelectedPm] = useState<string>(defaultPm);
  useEffect(() => { setSelectedPm(defaultPm); }, [defaultPm]);
  const usingSaved = selectedPm !== 'new' && cards.some((c) => c.stripe_payment_method_id === selectedPm);

  // Se o Stripe não está configurado (pk vazia), stripe resolve null.
  const [stripeMissing, setStripeMissing] = useState(false);
  useEffect(() => {
    let alive = true;
    stripePromise.then((s) => { if (alive && !s) setStripeMissing(true); });
    return () => { alive = false; };
  }, []);

  const submit = async () => {
    if (!stripe) return;
    setSubmitting(true);
    setError('');
    try {
      // Cartão SALVO: confirma o MESMO PaymentIntent com o payment_method existente
      // (3DS tratado pelo Stripe). Não precisa do PaymentElement.
      if (usingSaved) {
        const res = await stripe.confirmCardPayment(target.clientSecret, { payment_method: selectedPm });
        if (res.error) { setError(res.error.message ?? t('billing.checkoutFailed')); return; }
        onSuccess?.();
        return;
      }
      // Cartão NOVO / outro método: via PaymentElement.
      if (!elements) return;
      const confirmParams = { return_url: target.returnUrl };
      const res = target.mode === 'setup'
        ? await stripe.confirmSetup({ elements, confirmParams, redirect: 'if_required' })
        : await stripe.confirmPayment({ elements, confirmParams, redirect: 'if_required' });
      if (res.error) { setError(res.error.message ?? t('billing.checkoutFailed')); return; }
      onSuccess?.();
    } catch {
      setError(t('billing.checkoutFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (stripeMissing) {
    return (
      <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
        {t('billing.checkoutNotConfigured')}
      </p>
    );
  }

  const showButton = usingSaved || (selectedPm === 'new' && (ready || cards.length === 0));

  return (
    <form
      onSubmit={(ev) => { ev.preventDefault(); void submit(); }}
      className="flex flex-col gap-4"
    >
      {/* Cartões salvos (modo pagamento) — o PRINCIPAL vem selecionado. */}
      {cards.length > 0 && (
        <div className="flex flex-col gap-2">
          {cards.map((c) => {
            const on = selectedPm === c.stripe_payment_method_id;
            return (
              <button
                type="button"
                key={c.stripe_payment_method_id}
                onClick={() => setSelectedPm(c.stripe_payment_method_id)}
                className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${on ? 'border-primary bg-primary/5' : 'border-input hover:bg-accent'}`}
              >
                <span className={`grid size-4 place-items-center rounded-full border ${on ? 'border-primary' : 'border-muted-foreground/40'}`}>
                  {on && <span className="size-2 rounded-full bg-primary" />}
                </span>
                <CreditCard className="size-5 text-muted-foreground" />
                <span className="flex flex-1 flex-wrap items-center gap-1.5 text-sm">
                  <span className="font-medium capitalize">{c.brand}</span> ···· {c.last4}
                  {c.is_default && <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"><Star className="size-3" />{t('billing.cardPrincipal')}</span>}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setSelectedPm('new')}
            className={`flex items-center gap-3 rounded-xl border p-3 text-left text-sm transition-colors ${selectedPm === 'new' ? 'border-primary bg-primary/5' : 'border-input hover:bg-accent'}`}
          >
            <span className={`grid size-4 place-items-center rounded-full border ${selectedPm === 'new' ? 'border-primary' : 'border-muted-foreground/40'}`}>
              {selectedPm === 'new' && <span className="size-2 rounded-full bg-primary" />}
            </span>
            {t('billing.otherPaymentMethod')}
          </button>
        </div>
      )}

      {/* PaymentElement — só quando "cartão novo/outro método". */}
      {selectedPm === 'new' && (
        <>
          {!ready && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
            </div>
          )}
          <div className={ready ? 'block' : 'hidden'}>
            <PaymentElement onReady={() => setReady(true)} options={{ layout: 'tabs' }} />
          </div>

          {ready && target.showSaveToggle && target.mode === 'payment' && (
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
              <input
                type="checkbox"
                className="mt-0.5 size-4 accent-[#e0780c]"
                checked={saveCard}
                onChange={(ev) => { setSaveCard(ev.target.checked); onSaveCardChange?.(ev.target.checked); }}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{t('billing.saveCardToggle')}</span>
                <span className="block text-xs text-muted-foreground">{t('billing.saveCardHint')}</span>
              </span>
            </label>
          )}
        </>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {showButton && (
        <Button type="submit" disabled={!stripe || submitting} className="w-full">
          {submitting ? <Loader2 className="size-4 animate-spin" /> : <Lock className="size-4" />}
          {target.confirmLabel ?? t('billing.checkoutConfirm')}
        </Button>
      )}
      <p className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
        <Lock className="size-3" /> {t('billing.checkoutSecure')}
      </p>
    </form>
  );
}
